import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/lib/cart";

const LINKS = [
  { to: "/", label: "Startseite" },
  { to: "/reparaturen", label: "Reparaturen" },
  { to: "/ankauf", label: "Handy-Ankauf" },
  { to: "/refurbished", label: "Refurbished" },
  { to: "/zubehoer", label: "Zubehör" },
  { to: "/zusatzleistungen", label: "Zusatzleistungen" },
  { to: "/ueber-uns", label: "Über Uns" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setOpen(false);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav" : "bg-transparent"}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center shrink-0" data-testid="logo-link" onClick={closeMenu}>
          <img src="/logo.png" alt="Haus des Handys" className="h-9 md:h-10 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `px-3 xl:px-4 py-2 text-sm rounded-full transition-colors whitespace-nowrap ${
                  isActive ? "text-white bg-white/10" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setCartOpen(true)}
            className="relative p-2.5 rounded-full hover:bg-white/5 text-zinc-300 hover:text-white transition-colors"
            data-testid="header-cart-btn"
            aria-label="Warenkorb"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC1928] text-white text-[10px] font-semibold flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>

          <Link to="/reparaturen#configurator" data-testid="header-cta" className="hidden lg:block ml-2">
            <Button className="rounded-full bg-[#DC1928] hover:bg-[#B01829] text-white px-5 h-10 btn-lift glow-red-sm">
              Reparatur anfragen
            </Button>
          </Link>

          <button onClick={() => setOpen(!open)} className="lg:hidden p-2 text-white" data-testid="mobile-menu-toggle" aria-label="Menü">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/5 bg-[#0A0A0B]" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={closeMenu}
                data-testid={`mobile-nav-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-base ${isActive ? "bg-white/10 text-white" : "text-zinc-400"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/reparaturen#configurator" onClick={closeMenu} data-testid="mobile-cta">
              <Button className="mt-2 w-full rounded-full bg-[#DC1928] hover:bg-[#B01829] text-white h-11">
                Reparatur anfragen
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
